import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useFamily } from '../../context/FamilyContext';

interface DeleteLearnerDialogProps {
  learnerId: string;
  learnerFirstName: string;
  onDeleted?: () => void;
}

/**
 * Phase 7 (hardening) - the parent-facing "delete my child's data" control.
 * Deliberately stronger than every other confirmation dialog in this
 * codebase (see OverrideControl.tsx for the routine, low-friction pattern
 * used elsewhere): this starts a 30-day grace period after which the
 * learner, their evidence, and their mastery records are permanently
 * hard-deleted (scripts/deleteLearnerData.js) - genuinely irreversible once
 * that window elapses. The confirm button stays disabled until the
 * learner's first name is typed exactly (case-insensitive), and the dialog
 * explains the grace period and the restore path explicitly.
 */
const DeleteLearnerDialog: React.FC<DeleteLearnerDialogProps> = ({
  learnerId,
  learnerFirstName,
  onDeleted,
}) => {
  const { deleteLearner } = useFamily();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openDialog = () => {
    setConfirmText('');
    setError(null);
    setOpen(true);
  };

  const isConfirmed = confirmText.trim().toLowerCase() === learnerFirstName.trim().toLowerCase();

  const handleDelete = async () => {
    if (!isConfirmed) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteLearner(learnerId);
      setOpen(false);
      onDeleted?.();
    } catch (err) {
      console.error('Error deleting learner:', err);
      setError('Something went wrong removing this profile. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={openDialog}
        sx={{ textTransform: 'none' }}
      >
        Delete {learnerFirstName}'s data
      </Button>

      <Dialog open={open} onClose={() => !deleting && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete {learnerFirstName}'s data</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This removes {learnerFirstName} from your dashboard immediately. Their profile,
            progress, and history are then permanently deleted after 30 days. You can restore
            {' '}{learnerFirstName} from Settings any time before then - after 30 days, this
            cannot be undone.
          </Alert>
          {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Type <strong>{learnerFirstName}</strong> to confirm.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={learnerFirstName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={!isConfirmed || deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteLearnerDialog;
